import axios from 'axios'
import { user_state } from '../states/user.state'
import { toast_state } from '../states/toast.state'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'

// @ts-ignore — REACT_APP_API_URL injectée au build pour staging/prod
let apiUrl = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:1234`

export const http = axios.create({
  baseURL: apiUrl,
  timeout: 60000,
})

// Raccourcis pour les requêtes authentifiées (évite de répéter le header partout)
export let authHeaders = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
})

// Classification des erreurs pour affichage contextuel
export type ErrorType = 'timeout' | 'network' | 'server' | 'unknown'

export let classifyError = (error: any): { type: ErrorType, message: string, code?: number } => {
  if (!error) return { type: 'unknown', message: 'Une erreur inattendue s\'est produite' }

  // Requête annulée (changement de page, navigation) — pas une vraie erreur
  if (axios.isCancel(error)) return { type: 'unknown', message: 'Requête annulée' }

  // Timeout axios
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return { type: 'timeout', message: 'Le serveur met trop de temps à répondre' }
  }

  // Pas de réponse = problème réseau
  if (error.code === 'ERR_NETWORK' || !error.response) {
    return { type: 'network', message: 'Impossible de contacter le serveur' }
  }

  // Erreur serveur (5xx)
  if (error.response?.status >= 500) {
    return { type: 'server', message: `Le serveur a rencontré une erreur (${error.response.status})`, code: error.response.status }
  }

  // Erreur client (4xx)
  if (error.response?.status >= 400) {
    return { type: 'server', message: `Erreur ${error.response.status}`, code: error.response.status }
  }

  return { type: 'unknown', message: 'Une erreur inattendue s\'est produite' }
}

// Retry uniquement sur erreurs réseau/timeout, pas sur 4xx
let shouldRetry = (failureCount: number, error: any): boolean => {
  if (failureCount >= 3) return false
  let { type } = classifyError(error)
  return type === 'timeout' || type === 'network'
}

export function useAuthQuery<T>(key: string, url: string, options?: { refetchInterval?: number }) {
  const [user, set_user] = useRecoilState(user_state)
  const setToast = useSetRecoilState(toast_state)
  const history = useHistory()

  return useQuery<T>(
    [key, url],
    async ({ signal }) => {
      const response = await http.get(url, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        signal,
      })
      return response.data
    },
    {
      onError: (error) => {
        // @ts-expect-error
        if (error.response?.status === 401) {
          set_user(null)
          history.replace('/login')
          setToast({ message: 'Session expirée — veuillez vous reconnecter', color: 'warning' })
        }
      },
      refetchInterval: options?.refetchInterval ?? 5 * 60 * 1000, // 5 min par défaut
      staleTime: options?.refetchInterval ?? 5 * 60 * 1000, // Aligné sur refetchInterval
      keepPreviousData: true,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
    },
  )
}
